import { useGetTransactionReviewList } from '@/hooks/requests/useTransactionReview';
import { TransactionReviewStatus } from '@/types/transactionReview';
import { useState } from 'react';
import { SVLoader, SVTable } from '../core';
import { SVTableRow } from '@/types/common';
import { getTransactionReviewDeliverySpeed } from '@/utils/transactionReview';
import { titleCase } from '@/utils/stringCaser';
import TransactionReviewAmount from './TransactionReviewAmount';
import { ROUTES } from '@/constants/routes';

type TransactionReviewListProps = {
  status: TransactionReviewStatus;
};

export default function TransactionReviewList({
  status,
}: TransactionReviewListProps) {
  const [page, setPage] = useState(0);
  const {
    transactionReviews,
    totalRecordCount,
    isTransactionsReviewListPending,
  } = useGetTransactionReviewList({ page, status });

  if (isTransactionsReviewListPending) {
    return <SVLoader />;
  }

  const rows: SVTableRow[] = transactionReviews?.map(transaction => {
    const {
      uuid,
      requestedAt,
      displayAmount,
      description,
      reviewReasons,
      deliverySpeed,
      owner,
    } = transaction;
    return {
      key: uuid,
      cells: [
        {
          key: `date${uuid}`,
          content: requestedAt,
          type: 'date',
        },
        {
          key: `amount${uuid}`,
          content: <TransactionReviewAmount amount={displayAmount} />,
          type: 'custom',
        },
        {
          key: `description${uuid}`,
          content: description,
          type: 'text',
        },
        {
          key: `reason${uuid}`,
          content: titleCase(reviewReasons.join(', ')),
          type: 'text',
        },
        {
          key: `speed${uuid}`,
          content: getTransactionReviewDeliverySpeed(deliverySpeed),
          type: 'text',
        },
        {
          key: `owner${uuid}`,
          content: `${owner.userName} (${owner.userEmail})`,
          type: 'text',
        },
        {
          key: `details${uuid}`,
          type: 'link',
          content: `${ROUTES.TRANSACTION_REVIEW}/${uuid}`,
        },
      ],
    };
  });

  return (
    <SVTable
      ariaLabel={`Transaction ${status} Review`}
      headers={[
        { key: 'date', content: 'Date' },
        { key: 'amount', content: 'Amount' },
        { key: 'description', content: 'Description' },
        { key: 'reason', content: 'Review Reason' },
        { key: 'speed', content: 'Speed' },
        { key: 'owner', content: 'Owner' },
        { key: 'details', content: '' },
      ]}
      rows={rows}
      hasPagination
      page={page}
      setPage={setPage}
      totalRecordCount={totalRecordCount}
    />
  );
}
